import { create } from "zustand";

/**
 * Vault session + master-key store.
 *
 * SECURITY: the AES master key is the crown-jewel secret for this zero-knowledge
 * product, so it is held **in memory only** — never written to localStorage or
 * sessionStorage, where any script (e.g. an XSS payload) could read it and where
 * it would survive a reload. As a consequence, reloading the tab re-locks the
 * vault and the owner/heir must unlock again. The session token is likewise kept
 * in memory only.
 *
 * An idle timeout and a visibility/unload lock (see `installAutoLock`) further
 * bound the window during which the key lives in memory.
 */

interface VaultState {
  vaultSessionToken: string;
  masterKey: Uint8Array | null;
  isUnlocked: boolean;
  /** ms epoch of the last user activity while unlocked; drives the idle timeout. */
  lastActivityAt: number;
  setVaultSession: (token: string, masterKey: Uint8Array) => void;
  touch: () => void;
  lock: () => void;
}

export const useVaultStore = create<VaultState>((set) => ({
  vaultSessionToken: "",
  masterKey: null,
  isUnlocked: false,
  lastActivityAt: 0,

  setVaultSession: (token, masterKey) => {
    set({
      vaultSessionToken: token,
      masterKey,
      isUnlocked: true,
      lastActivityAt: Date.now(),
    });
  },

  touch: () => {
    // Only record activity while unlocked so a locked tab can't keep itself alive.
    set((s) => (s.isUnlocked ? { lastActivityAt: Date.now() } : {}));
  },

  lock: () => {
    // Best-effort zeroisation of the in-memory key before dropping the reference.
    const key = useVaultStore.getState().masterKey;
    if (key) key.fill(0);
    set({
      vaultSessionToken: "",
      masterKey: null,
      isUnlocked: false,
      lastActivityAt: 0,
    });
  },
}));

export const getVaultSessionToken = () =>
  useVaultStore.getState().vaultSessionToken;
export const getMasterKey = () => useVaultStore.getState().masterKey;
export const isVaultUnlocked = () => useVaultStore.getState().isUnlocked;

/** Idle window after which an unlocked vault auto-locks. */
export const IDLE_LOCK_MS = 15 * 60 * 1000; // 15 minutes

/**
 * Wire up auto-lock behaviour. Call once at app startup (see `main.tsx`).
 *
 *  - locks when the tab is hidden/closed (`visibilitychange`, `pagehide`);
 *  - locks after {@link IDLE_LOCK_MS} of no user interaction;
 *  - user interaction (`pointerdown`/`keydown`) refreshes the idle timer.
 *
 * Returns a teardown function (useful in tests / HMR).
 */
export function installAutoLock(): () => void {
  if (typeof window === "undefined") return () => {};

  const { lock, touch } = useVaultStore.getState();

  const onActivity = () => touch();
  const onHide = () => {
    // `visibilitychange` also fires on tab switch — only lock when actually hidden.
    if (document.visibilityState === "hidden") lock();
  };
  const onPageHide = () => lock();

  const idleTimer = window.setInterval(() => {
    const { isUnlocked, lastActivityAt } = useVaultStore.getState();
    if (isUnlocked && Date.now() - lastActivityAt >= IDLE_LOCK_MS) {
      lock();
    }
  }, 30 * 1000);

  window.addEventListener("pointerdown", onActivity, { passive: true });
  window.addEventListener("keydown", onActivity, { passive: true });
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", onPageHide);

  return () => {
    window.clearInterval(idleTimer);
    window.removeEventListener("pointerdown", onActivity);
    window.removeEventListener("keydown", onActivity);
    document.removeEventListener("visibilitychange", onHide);
    window.removeEventListener("pagehide", onPageHide);
  };
}
